import type { ICellRendererParams } from 'ag-grid-community'
import { createTemplateRenderer } from '../utils/fast-renderer'

// Progress data type from backend
type ProgressData = {
  readonly value: string | number
  readonly color?: string
}

// Progress color options for type safety
type ProgressColor =
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | string

// Color mapping to Radix scale indices for CSS variable resolution
const COLOR_SCALE_MAP: Record<
  string,
  { ring: number; bg: number; text: number }
> = {
  brand: { ring: 7, bg: 1, text: 9 },
  success: { ring: 7, bg: 2, text: 9 },
  warning: { ring: 7, bg: 2, text: 9 },
  danger: { ring: 7, bg: 2, text: 9 },
  info: { ring: 7, bg: 2, text: 9 }
}

// Map generic color names to actual CSS variable names
const COLOR_NAME_MAP: Record<string, string> = {
  brand: 'brand',
  success: 'green',
  warning: 'amber',
  danger: 'red',
  info: 'cyan'
}

// r=8 → full ring length; the dash offset encodes the filled fraction.
const CIRCUMFERENCE = 2 * Math.PI * 8

/**
 * Static progress skeleton — parsed ONCE, cloned per cell. A 20x20 ring SVG
 * (background circle + foreground ring) and a `%` label. The HTML parser places
 * `<svg>`/`<circle>` in the SVG namespace on clone, so per-cell we only write
 * the dynamic stroke offset, the colour CSS vars, and the label text.
 */
const PROGRESS_TEMPLATE =
  '<div class="flex items-center gap-2">' +
  '<svg width="20" height="20" viewBox="0 0 20 20" style="flex-shrink:0">' +
  '<circle class="progress-bg" cx="10" cy="10" r="8" fill="none" stroke-width="3"></circle>' +
  `<circle class="progress-ring" cx="10" cy="10" r="8" fill="none" stroke-width="3" stroke-linecap="round" stroke-dasharray="${CIRCUMFERENCE}" transform="rotate(-90 10 10)"></circle>` +
  '</svg>' +
  '<span class="progress-label text-xs font-medium"></span>' +
  '</div>'

/**
 * Apply progress data to a cloned skeleton in place — no re-parse, no re-clone.
 *
 * Execution flow:
 * 1. Empty/non-object data → blank cell (clear the label) and bail.
 * 2. Recompute the ring's `stroke-dashoffset` from the numeric value.
 * 3. Resolve and write the bg/ring stroke + text colour CSS vars (same Radix
 *    tokens the former `progressHTML` emitted).
 * 4. Set the `%` label via textContent.
 */
const updateProgress = (
  root: HTMLElement,
  data: ProgressData | null | undefined
): void => {
  const label = root.querySelector('.progress-label')! as HTMLElement

  if (!data || typeof data !== 'object') {
    label.textContent = ''
    return
  }

  const numValue = Number(data.value ?? 0)
  const colorKey = data.color ?? 'brand'
  const colorName = COLOR_NAME_MAP[colorKey] ?? 'brand'
  const scales = COLOR_SCALE_MAP[colorKey] ?? COLOR_SCALE_MAP.brand

  const offset = CIRCUMFERENCE - (numValue / 100) * CIRCUMFERENCE

  const bg = root.querySelector('.progress-bg')!
  const ring = root.querySelector('.progress-ring')!

  bg.setAttribute('style', `stroke:var(--color-${colorName}-${scales.bg})`)
  ring.setAttribute('style', `stroke:var(--color-${colorName}-${scales.ring})`)
  ring.setAttribute('stroke-dashoffset', String(offset))

  label.style.color = `var(--color-${colorName}-${scales.text})`
  label.textContent = `${numValue}%`
}

/** Progress cell renderer — clones a pre-parsed template, no innerHTML on refresh */
export const ProgressComponent = createTemplateRenderer<
  ProgressData | null | undefined
>(PROGRESS_TEMPLATE, (root, data, _params: ICellRendererParams) =>
  updateProgress(root, data)
)

// Export types for external use
export type { ProgressColor, ProgressData }
